import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";
import { useMovieQuery } from "@/hooks/useMovieQuery";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieFilterBar from "@/components/feature/MovieFilterBar";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import CategorySidebar from "@/pages/category/components/CategorySidebar";

const PAGE_SIZE = 8;

export default function Category() {
  useSeo({
    title: "电影分类 - 高清影视资源 | 《出轨的妻子》hd",
    description:
      "《出轨的妻子》hd 电影分类页，汇集剧情、爱情、悬疑、犯罪等类型高清影视资源，支持按地区、年份组合筛选与排序，快速找到想看的电影。",
    keywords: "电影分类,高清影视资源,出轨的妻子,剧情片,爱情片,悬疑片",
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const genre = searchParams.get("genre") || "全部";
  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "全部";
  const sort = searchParams.get("sort") || "default";
  const page = Number(searchParams.get("page") || "1");

  const filtered = useMovieQuery(movies, { genre, region, year, sort });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);

  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage],
  );

  const updateParams = (
    patch: Record<string, string>,
    options: { resetPage?: boolean } = { resetPage: true },
  ) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "全部" || (key === "sort" && value === "default")) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    if (options.resetPage) next.delete("page");
    setSearchParams(next);
  };

  const resetAll = () => setSearchParams(new URLSearchParams());

  const handleFilterChange = (patch: { genre?: string; region?: string; year?: string; sort?: string }) =>
    updateParams(patch);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影分类" }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">高清电影分类</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          按类型、地区与年份组合筛选，配合排序快速定位你感兴趣的影视资源。当前共收录
          <strong className="mx-1 font-semibold text-primary-600">{filtered.length}</strong>部符合条件的电影。
        </p>
      </header>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div>
          <MovieFilterBar
            genre={genre}
            region={region}
            year={year}
            sort={sort}
            onChange={handleFilterChange}
            onReset={resetAll}
          />

          {paged.length > 0 ? (
            <>
              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5">
                {paged.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination
                current={currentPage}
                totalPages={totalPages}
                onChange={(p) => updateParams({ page: String(p) }, { resetPage: false })}
              />
            </>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-movie-line text-3xl" aria-hidden="true"></i>
              </span>
              <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-900">暂无符合条件的电影</h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                试试减少筛选条件，或直接重置筛选查看全部影视资源。
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>

        <CategorySidebar activeGenre={genre} />
      </div>
    </div>
  );
}